import { useState } from 'react';
import FXRateList from '../../components/admin/fx/FXRateList';
import FXRateForm from '../../components/admin/fx/FXRateForm';
import FXRateImport from '../../components/admin/fx/FXRateImport';
import FXDepreciationForm from '../../components/admin/fx/FXDepreciationForm';
import Button from '../../components/shared/UI/Button';

/**
 * Admin editor for the /fx_year_end_rates registry — one YEAR-END USD rate per
 * reporting currency per calendar year.
 *
 * Follows the CountriesPage shape: a `view` state over list/add/edit, plus a
 * refresh token so the list reloads after a write. There is no delete view:
 * Firestore rules block deletes on this collection so rate history cannot be
 * destroyed (Decision 4).
 *
 * Nothing consumes these rates yet — the first consumer is the USD-per-year
 * CAGR formula.
 */
export default function FXRatesPage() {
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  function backToList() {
    setView('list');
    setSelected(null);
  }

  function handleSaved() {
    backToList();
    setRefreshKey(k => k + 1);
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Year-end FX Rates</h1>
          <p>
            Year-end USD rates per reporting currency, used to express figures in USD.
            All fiscal years end in December, so each year&apos;s rate is that year&apos;s
            December close.
          </p>
        </div>
        {view === 'list' && (
          <Button variant="secondary" onClick={() => setView('depreciation')}>
            Set depreciation rate
          </Button>
        )}
      </div>

      {view === 'list' && (
        <FXRateList
          refreshToken={refreshKey}
          onAdd={() => { setSelected(null); setView('add'); }}
          onEdit={(row) => { setSelected(row); setView('edit'); }}
          onImport={() => setView('import')}
        />
      )}

      {(view === 'add' || view === 'edit') && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <Button variant="ghost" onClick={backToList}>← Back to Rates</Button>
          </div>
          <div className="card">
            <div className="card-header">
              <h2>
                {view === 'add'
                  ? 'Add Year-end Rate'
                  : `Edit: ${selected?.currency} ${selected?.year}`}
              </h2>
            </div>
            <div className="card-body">
              <FXRateForm
                entry={view === 'edit' ? selected : null}
                onSaved={handleSaved}
                onCancel={backToList}
              />
            </div>
          </div>
        </div>
      )}

      {view === 'import' && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <Button variant="ghost" onClick={backToList}>← Back to Rates</Button>
          </div>
          <div className="card">
            <div className="card-header">
              <h2>Import Year-end Rates</h2>
            </div>
            <div className="card-body">
              <FXRateImport onDone={handleSaved} onCancel={backToList} />
            </div>
          </div>
        </div>
      )}

      {view === 'depreciation' && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <Button variant="ghost" onClick={backToList}>← Back to Rates</Button>
          </div>
          <div className="card">
            <div className="card-header">
              <h2>Set Annual Depreciation Rate</h2>
            </div>
            <div className="card-body">
              <FXDepreciationForm onSaved={handleSaved} onCancel={backToList} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
